import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "type", "disabled", "forwardedAs", "as", "kind", "size", "loading", "fluid"];
import PropTypes from 'prop-types';
import React, { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { HighlightRegion, useRegion } from '../regions';
import { FocusStyle, ScopedCssBaseline } from '../StyleBase';
import { SIZES } from '../helper';
import { kinds, types } from './constant';
import KindStyle from './KindStyle';
const Root = styled.button.withConfig({
  displayName: "ButtonBase__Root",
  componentId: "sc-3vs9dj-0"
})(["", " ", ";border-radius:var(--border-radius-rounded);cursor:pointer;user-select:none;display:inline-flex;justify-content:center;align-items:center;vertical-align:middle;position:relative;margin:0;font:var(--text-md-bold);padding:0 var(--size-spacing-04);height:40px;transition-property:background-color,border-color;transition-duration:var(--motion-duration-extra-fast);transition-timing-function:var(--motion-timing-function-ease-in-out);border:none;background-color:transparent;white-space:nowrap;", " ", " ", " ", ""], ScopedCssBaseline, FocusStyle, _ref => {
  let {
    $size,
    $region
  } = _ref;
  return ($size === SIZES.SM || $region === HighlightRegion.displayName) && css(["padding:0 var(--size-spacing-03);font:var(--text-sm-bold);height:32px;"]);
}, KindStyle, _ref2 => {
  let {
    as
  } = _ref2;
  return as === 'a' && css(["text-decoration:none;"]);
}, _ref3 => {
  let {
    $fluid
  } = _ref3;
  return $fluid && css(["width:100%;"]);
});
const ButtonBase = /*#__PURE__*/forwardRef((_ref4, ref) => {
  let {
      children,
      type,
      disabled,
      forwardedAs,
      as,
      kind,
      size,
      loading,
      fluid
    } = _ref4,
    rest = _objectWithoutPropertiesLoose(_ref4, _excluded);
  const region = useRegion();
  const buttonProps = (forwardedAs || as) === 'button' ? {
    type
  } : {};
  return /*#__PURE__*/React.createElement(Root, _extends({
    $region: region,
    $kind: kind,
    $size: size,
    $loading: loading,
    $fluid: fluid,
    as: as,
    forwardedAs: forwardedAs,
    ref: ref
  }, buttonProps, {
    disabled: disabled || loading,
    "aria-disabled": disabled || loading
  }, rest), children);
});
ButtonBase.Style = Root;
ButtonBase.sizes = {
  sm: SIZES.SM,
  md: SIZES.MD
};
ButtonBase.kinds = kinds;
ButtonBase.types = types;
ButtonBase.defaultProps = {
  children: '',
  type: ButtonBase.types.button,
  kind: ButtonBase.kinds.primary,
  size: ButtonBase.sizes.md,
  as: 'button',
  forwardedAs: undefined,
  loading: false,
  fluid: false
};
process.env.NODE_ENV !== "production" ? ButtonBase.propTypes = {
  /**
   * Set element type to be rendered for this component. By default it will render a `button` element.
   */
  as: PropTypes.oneOfType([PropTypes.elementType, PropTypes.string]),
  /**
   * Set element type with a new style to be rendered for this component. By default it will render a `button` element.
   */
  forwardedAs: PropTypes.oneOfType([PropTypes.elementType, PropTypes.string]),
  /**
   * Sets button children
   */
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.string, PropTypes.array, PropTypes.node]),
  /**
   * Sets button type
   */
  type: PropTypes.oneOf([ButtonBase.types.button, ButtonBase.types.submit, ButtonBase.types.reset]),
  /**
   * Sets button kind for styling
   */
  kind: PropTypes.oneOf([ButtonBase.kinds.primary, ButtonBase.kinds.secondary, ButtonBase.kinds.tertiary, ButtonBase.kinds.neutral, ButtonBase.kinds.danger]),
  /**
   * Sets the button size
   */
  size: PropTypes.oneOf([ButtonBase.sizes.sm, ButtonBase.sizes.md]),
  /**
   * Sets loading appearance. When loading is true, then disabled is true.
   */
  loading: PropTypes.bool,
  /**
   * Makes Button full-width
   */
  fluid: PropTypes.bool
} : void 0;
export default ButtonBase;